import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/ui/components/Button';
import { ToggleSwitchCard } from '@/ui/components/ToggleSwitchCard';
import { TimesIcon } from '@/assets/icons';
import type { CookiePreferencesState } from '../../types';
import styles from './CookiePreferences.module.scss';
import { externalUrls } from '@/constants/urls';
import { Link } from 'react-router-dom';
import { useCookie } from '@/providers/Cookie';

export interface CookiePreferencesProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when modal open state changes
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Callback when "Accept all cookies" button is clicked
   */
  onAcceptAll?: () => void;

  /**
   * Callback when "Accept settings" button is clicked
   */
  onAcceptSettings?: (preferences: CookiePreferencesState) => void;

  /**
   * URL for cookie policy link
   */
  cookiePolicyUrl?: string;

  /**
   * Test ID for testing
   */
  testId?: string;
}

const defaultPreferences: CookiePreferencesState = {
  necessary: true, // Always active
  marketing: false,
  functional: false,
  analytics: false,
};

export function CookiePreferences({
  open,
  onOpenChange,
  className,
  onAcceptAll,
  onAcceptSettings,
  testId,
}: CookiePreferencesProps) {
  const { preferences: existingPreferences } = useCookie();

  const [preferences, setPreferences] = useState<CookiePreferencesState>(
    existingPreferences || defaultPreferences
  );

  // Sync preferences from cookies when modal opens or existing preferences change
  useEffect(() => {
    if (open) {
      if (existingPreferences) {
        setPreferences(existingPreferences);
      } else {
        setPreferences(defaultPreferences);
      }
    }
  }, [open, existingPreferences]);

  const handlePreferenceChange = (
    key: keyof CookiePreferencesState,
    checked: boolean
  ) => {
    if (key === 'necessary') {
      // Necessary cookies are always active
      return;
    }
    setPreferences((prev) => ({ ...prev, [key]: checked }));
  };

  const handleAcceptAll = () => {
    setPreferences({
      necessary: true,
      marketing: true,
      functional: true,
      analytics: true,
    });
    onAcceptAll?.();
    onOpenChange(false);
  };

  const handleAcceptSettings = () => {
    onAcceptSettings?.(preferences);
    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const containerClasses = clsx(styles.container, className);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={containerClasses} data-testid={testId}>
          <VisuallyHidden>
            <Dialog.Title>Setări Cookies</Dialog.Title>
            <Dialog.Description>
              Configurează preferințele tale pentru cookie-uri
            </Dialog.Description>
          </VisuallyHidden>

          {/* Close Button */}
          <Dialog.Close asChild>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Close cookie preferences"
            >
              <TimesIcon className={styles.closeIcon} />
            </button>
          </Dialog.Close>

          {/* Scrollable Content */}
          <div className={styles.scrollable}>
            {/* Title Section */}
            <div className={styles.titleSection}>
              <h2 className={styles.title}>Setări Cookies</h2>
              <p className={styles.description}>
                Atunci când vizitezi website-uri, acestea pot stoca sau prelua
                date în browser-ul tău. Această stocare adesea este necesară
                pentru funcționarea de bază a website-ului. Stocarea poate fi
                făcută în scopuri de marketing, analiză și personalizare a
                site-ului, cum ar fi stocarea preferințelor. Confidențialitatea
                este importantă pentru noi, așa că ai opțiunea să dezactivezi
                anumite tipuri de stocare ce nu sunt necesare la funcționarea de
                bază a site-ului. Blocarea anumitor categorii îți poate impacta
                experiența pe site. Află mai multe din{' '}
                <Link
                  to={externalUrls.volt.cookiePolicy}
                  className={styles.link}
                  target="_blank"
                >
                  Politica de cookies
                </Link>
              </p>
            </div>

            {/* Cookie Preferences */}
            <div className={styles.preferences}>
              <div
                className={clsx(styles.preferenceItem, styles.necessaryCard)}
              >
                <ToggleSwitchCard
                  label="Cookie-uri strict necesare (Mereu active)"
                  checked={preferences.necessary}
                  disabled={true}
                  onChange={(checked) =>
                    handlePreferenceChange('necessary', checked)
                  }
                  testId={`${testId}-necessary`}
                  className={styles.necessaryTermsCard}
                  labelClassName={styles.necessaryLabel}
                />
                <p className={styles.preferenceDescription}>
                  Acestea sunt obligatorii pentru a permite funcționarea de bază
                  a website-ului.
                </p>
              </div>

              <div className={styles.preferenceItem}>
                <ToggleSwitchCard
                  label="Cookie-uri de promovare"
                  checked={preferences.marketing}
                  onChange={(checked) =>
                    handlePreferenceChange('marketing', checked)
                  }
                  testId={`${testId}-marketing`}
                />
                <p className={styles.preferenceDescription}>
                  Aceste cookie-uri sunt folosite pentru a-ți livra campanii de
                  promovare care ți se potrivesc mai bine ție și intereselor
                  tale. Acestea pot fi, de asemenea, folosite pentru a limita de
                  câte ori vezi o reclamă și pentru a măsura eficiența
                  campaniilor publicitare. De obicei, rețelele de publicitate le
                  plasează cu permisiunea operatorului website-ului.
                </p>
              </div>

              <div className={styles.preferenceItem}>
                <ToggleSwitchCard
                  label="Cookie-uri funcționale"
                  checked={preferences.functional}
                  onChange={(checked) =>
                    handlePreferenceChange('functional', checked)
                  }
                  testId={`${testId}-functional`}
                />
                <p className={styles.preferenceDescription}>
                  Aceste cookie-uri permite website-ului să rețină alegerile pe
                  care le faci - de exemplu numele, limba sau regiunea în care
                  ești - și să-ți ofere funcționalități îmbunătățite,
                  personalizate. De exemplu, un website îți poate furniza
                  rapoarte meteo locale sau știri din trafic prin stocarea de
                  date privind locația ta curentă.
                </p>
              </div>

              <div className={styles.preferenceItem}>
                <ToggleSwitchCard
                  label="Cookie-uri de analiză și performanță"
                  checked={preferences.analytics}
                  onChange={(checked) =>
                    handlePreferenceChange('analytics', checked)
                  }
                  testId={`${testId}-analytics`}
                />
                <p className={styles.preferenceDescription}>
                  Aceste cookie-uri ajută operatorul website-ului să înțeleagă
                  cum performează website-ul lui, cum interacționează
                  vizitatorii cu site-ul și să identifice eventuale probleme
                  tehnice. Tipul acesta de stocare de obicei nu colectează
                  informații care să identifice vizitatorul.
                </p>
              </div>
            </div>
          </div>

          {/* Buttons Section */}
          <div className={styles.buttons}>
            <Button
              trackingId="accept_all_cookies_preferences"
              variant="filled"
              size="medium"
              text="Accept toate cookie-urile"
              onClick={handleAcceptAll}
              className={styles.acceptAllButton}
              data-testid={testId ? `${testId}-accept-all` : undefined}
            />
            <Button
              trackingId="save_cookie_preferences"
              variant="outlined"
              size="medium"
              text="Accept setările"
              onClick={handleAcceptSettings}
              className={styles.acceptSettingsButton}
              data-testid={testId ? `${testId}-accept-settings` : undefined}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default CookiePreferences;
